// packages/console/src/hooks/useLiveControl.ts
//
// Owns the LIVE control plane the console drives: the workspace's provisioned
// streams, the selected one and its ingest URL + stream key, the lifecycle
// actions (arm / stop / pause / resume), and its push destinations. It is the
// command side; the telemetry SSE (useLiveStream) is the read side that lights
// up once a broadcaster connects.
//
// Honest state (R10): a failed control call surfaces its message and leaves the
// UI unchanged rather than fabricating a transition. `armed` is optimistic local
// state (set when arm succeeds, cleared on stop) so the button reflects the
// operator's intent before the source connects; the authoritative on-air signal
// stays the telemetry stream.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDestination,
  createStream,
  deleteDestination,
  listDestinations,
  listStreams,
  pauseStream,
  resumeStream,
  setStreamKey,
  srtIngestUrl,
  startStream,
  stopStream,
  type AddDestinationInput,
  type LiveDestinationRec,
  type LiveStreamRec,
} from "../api/liveControl";

export interface LiveControlState {
  streams: LiveStreamRec[];
  current: LiveStreamRec | null;
  selectStream: (id: string) => void;
  create: (name: string, presetId: string) => Promise<void>;
  setKey: (id: string, streamKey: string) => Promise<LiveStreamRec>;
  arm: () => Promise<void>;
  stop: () => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  // disarm drops the optimistic armed bridge once telemetry becomes
  // authoritative (the stream is on air, or has ended), so the control follows
  // real state rather than a stale intent.
  disarm: () => void;
  destinations: LiveDestinationRec[];
  addDest: (input: AddDestinationInput) => Promise<void>;
  removeDest: (destId: string) => Promise<void>;
  ingestUrl: string | null;
  streamKey: string | null;
  armed: boolean;
  busy: boolean;
  loading: boolean;
  errorMessage: string | null;
  reload: () => void;
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : "live control request failed";
}

// mostRecent returns the newest stream by createdAt, so a workspace with several
// provisioned streams defaults to the one it most likely just made.
function mostRecent(streams: LiveStreamRec[]): LiveStreamRec | null {
  if (streams.length === 0) return null;
  return streams.reduce((a, b) => (a.createdAt >= b.createdAt ? a : b));
}

export function useLiveControl(): LiveControlState {
  const [streams, setStreams] = useState<LiveStreamRec[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [destinations, setDestinations] = useState<LiveDestinationRec[]>([]);
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setError] = useState<string | null>(null);
  const [reloadTick, setReload] = useState(0);

  // Load the workspace's streams once (and on reload), selecting the most recent
  // when nothing is selected yet.
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    listStreams(ctrl.signal)
      .then((list) => {
        if (!alive) return;
        setStreams(list);
        setCurrentId((cur) => cur ?? mostRecent(list)?.id ?? null);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        if (ctrl.signal.aborted) return;
        setError(errMessage(err));
        setLoading(false);
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [reloadTick]);

  const current = useMemo(
    () => streams.find((s) => s.id === currentId) ?? null,
    [streams, currentId],
  );

  // Load the selected stream's destinations whenever it changes.
  useEffect(() => {
    if (!currentId) {
      setDestinations([]);
      return;
    }
    let alive = true;
    const ctrl = new AbortController();
    listDestinations(currentId, ctrl.signal)
      .then((list) => {
        if (alive) setDestinations(list);
      })
      .catch((err) => {
        if (alive && !ctrl.signal.aborted) setError(errMessage(err));
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [currentId, reloadTick]);

  const selectStream = useCallback((id: string) => {
    setArmed(false);
    // Drop the previous stream's destinations immediately so the panel never
    // attributes them to the newly selected stream while its list loads.
    setDestinations([]);
    setCurrentId(id);
  }, []);

  const create = useCallback(async (name: string, presetId: string) => {
    setBusy(true);
    setError(null);
    try {
      const s = await createStream(name.trim() || "Live stream", presetId);
      setStreams((cur) => [s, ...cur]);
      setDestinations([]);
      setCurrentId(s.id);
      setArmed(false);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  }, []);

  // setKey persists a caller-chosen stream key and folds the returned record back
  // into state so the ingest URL rebuilds from it. It rethrows so the panel can
  // surface the validation/collision message inline on the field.
  const setKey = useCallback(async (id: string, streamKey: string) => {
    setBusy(true);
    setError(null);
    try {
      const s = await setStreamKey(id, streamKey);
      setStreams((cur) => cur.map((x) => (x.id === s.id ? s : x)));
      return s;
    } catch (err) {
      setError(errMessage(err));
      throw err;
    } finally {
      setBusy(false);
    }
  }, []);

  // runAction wraps a lifecycle call: it guards double-fire with `busy`, clears
  // the error on entry, and surfaces a failure without changing intent state.
  const runAction = useCallback(
    async (fn: (id: string) => Promise<unknown>, onOk: () => void) => {
      if (!currentId) return;
      setBusy(true);
      setError(null);
      try {
        await fn(currentId);
        onOk();
      } catch (err) {
        setError(errMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [currentId],
  );

  const arm = useCallback(() => runAction(startStream, () => setArmed(true)), [runAction]);
  const stop = useCallback(() => runAction(stopStream, () => setArmed(false)), [runAction]);
  const disarm = useCallback(() => setArmed(false), []);
  const pause = useCallback(() => runAction(pauseStream, () => {}), [runAction]);
  const resume = useCallback(() => runAction(resumeStream, () => {}), [runAction]);

  const addDest = useCallback(
    async (input: AddDestinationInput) => {
      if (!currentId) return;
      setBusy(true);
      setError(null);
      try {
        const d = await addDestination(currentId, input);
        setDestinations((cur) => [...cur, d]);
      } catch (err) {
        setError(errMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [currentId],
  );

  const removeDest = useCallback(
    async (destId: string) => {
      if (!currentId) return;
      setBusy(true);
      setError(null);
      try {
        await deleteDestination(currentId, destId);
        setDestinations((cur) => cur.filter((d) => d.id !== destId));
      } catch (err) {
        setError(errMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [currentId],
  );

  return {
    streams,
    current,
    selectStream,
    create,
    setKey,
    arm,
    stop,
    pause,
    resume,
    disarm,
    destinations,
    addDest,
    removeDest,
    ingestUrl: current ? srtIngestUrl(current.streamKey) : null,
    streamKey: current?.streamKey ?? null,
    armed,
    busy,
    loading,
    errorMessage,
    reload: () => setReload((t) => t + 1),
  };
}
